/**
 * Turns a /api/spiral/inspect response into chart overlays.
 *
 * Separate from spiralOverlays.js because the two draw different claims and must be removable
 * independently: the scan's band is "here is the strongest convergence in this series", while an
 * inspection is "here is what THIS bar is related to". Clearing one must not clear the other, and
 * sharing an overlay id is how that goes wrong.
 *
 * Three overlays, mirroring the scan's structure:
 *
 *   spiral-inspect-marks   a marker on the anchor and on every rung that landed
 *   spiral-inspect-lines   a vertical rule at each projected date
 *   spiral-inspect-arcs    a curve from anchor to target (FORWARD) or parent to target (BACKWARD)
 *
 * The colour carries the verdict, and this is the part worth getting right: a rung that landed on a
 * real turn, a rung that landed on nothing, and a rung that cannot have happened yet are three
 * different statements. Drawing all of them the same way would make a ladder of guesses look like a
 * ladder of confirmations.
 */

/**
 * Marks and arcs are scoped BY DIRECTION, so a forward ladder and a backward lineage can be on the
 * chart at once.
 *
 * They shared one id each until both directions could be asked for together, and sharing was fine
 * while only one could be drawn: the second inspection replaced the first because the ids collided,
 * which was the intended behaviour. It stops being intended the moment "both" is an option — the
 * forward marks would be silently overwritten by the backward ones and the user would see half of
 * what they asked for, with nothing to say so.
 */
export const SPIRAL_INSPECT_MARKS_IDS = {
  FORWARD: 'spiral-inspect-marks-forward',
  BACKWARD: 'spiral-inspect-marks-backward',
};
export const SPIRAL_INSPECT_ARCS_IDS = {
  FORWARD: 'spiral-inspect-arcs-forward',
  BACKWARD: 'spiral-inspect-arcs-backward',
};

/** The direction an inspection describes; anything not BACKWARD is a forward projection. */
export const directionOf = (inspection) =>
  (inspection?.mode === 'BACKWARD' ? 'BACKWARD' : 'FORWARD');

/**
 * The chart's vertical-rule overlay carries ONE date each, so a ladder needs one overlay per rung
 * and therefore one id per rung.
 *
 * The ids are a fixed list rather than derived from the current result, because clearing has to
 * remove the PREVIOUS inspection - which the caller no longer holds. A bounded list works because
 * the ladder itself is bounded: the backend's Fibonacci array is 26 entries, so 32 ids can never be
 * outrun. Deriving them from the result instead would leave the longest run's extra lines on the
 * chart the moment a shorter one replaced it.
 */
export const SPIRAL_INSPECT_LINE_COUNT = 32;
export const SPIRAL_INSPECT_LINE_IDS = Array.from(
  { length: SPIRAL_INSPECT_LINE_COUNT },
  (_, i) => `spiral-inspect-line-${i}`,
);

export const SPIRAL_INSPECT_OVERLAY_IDS = [
  ...Object.values(SPIRAL_INSPECT_MARKS_IDS),
  ...Object.values(SPIRAL_INSPECT_ARCS_IDS),
  ...SPIRAL_INSPECT_LINE_IDS,
];

/** Green confirms, grey declines to confirm, blue is not yet askable. */
export const OUTCOME_COLORS = {
  VERIFIED: '#2e7d32',
  UNCONFIRMED: '#9e9e9e',
  FUTURE: '#1565c0',
};

const ANCHOR_COLOR = '#f57c00';
const PARENT_HIGH_COLOR = '#c62828';
const PARENT_LOW_COLOR = '#2e7d32';
const ARC_COLOR = 'rgba(21, 101, 192, 0.85)';

export function colorForOutcome(outcome) {
  return OUTCOME_COLORS[outcome] || OUTCOME_COLORS.UNCONFIRMED;
}

/** The chart's date for a bar index, or null past either end. Never invents one. */
export function dateForBarIndex(dates, barIndex) {
  if (!Array.isArray(dates) || dates.length === 0) return null;
  if (barIndex < 0 || barIndex >= dates.length) return null;
  return dates[barIndex];
}

/**
 * A vertical rule at each projected date, coloured by what was found there.
 *
 * <p>Rungs landing past the last bar are dropped rather than clamped to the chart edge. A stack of
 * FUTURE lines piled on the final candle would read as a convergence at the right edge - the exact
 * false signal this feature exists to find honestly. The count of what was dropped is returned so
 * the caller can say so in words instead.
 */
export function buildForwardLineOverlays(inspection, dates) {
  const projections = inspection?.projections;
  if (!Array.isArray(projections) || projections.length === 0) return [];
  if (!Array.isArray(dates) || dates.length === 0) return [];

  const out = [];
  projections.forEach((p) => {
    const date = dateForBarIndex(dates, p.targetBarIndex);
    if (!date) return;
    if (out.length >= SPIRAL_INSPECT_LINE_COUNT) return;
    out.push({
      type: 'vLine',
      // Positional, so a shorter later run reuses the same ids and the leftovers are cleared by
      // the fixed list above rather than lingering.
      id: SPIRAL_INSPECT_LINE_IDS[out.length],
      date,
      color: colorForOutcome(p.outcome),
      // Verified rungs draw solid and everything else dashed, so the confirmed dates are legible
      // without consulting a legend. A rung that landed on nothing must not look like one that hit.
      lineType: p.outcome === 'VERIFIED' ? 'solid' : 'dashed',
      thickness: p.outcome === 'VERIFIED' ? 2 : 1,
      label: `F(${p.fibNumber})`,
      source: 'spiralInspect',
    });
  });
  return out;
}

/**
 * A marker on the anchor bar, plus one on every pivot a rung actually landed on.
 *
 * <p>The anchor is marked distinctly. Without it the user cannot see which bar they right-clicked,
 * and a set of target lines with no visible origin is a fan of dates with nothing to explain them.
 */
export function buildForwardMarkOverlay(inspection, dates) {
  const anchor = inspection?.anchor;
  if (!anchor) return null;

  const anchorDate = dateForBarIndex(dates, anchor.barIndex);
  if (!anchorDate) return null;

  const points = [{
    date: anchorDate,
    price: anchor.price,
    position: 'below',
    color: ANCHOR_COLOR,
    label: anchor.isPivot ? `Anchor · N=${anchor.pivot?.windowN ?? '?'}` : 'Anchor',
  }];

  const seen = new Set();
  (inspection.projections || []).forEach((p) => {
    if (p.outcome !== 'VERIFIED' || !p.matchedPivot) return;
    const hit = p.matchedPivot;
    if (seen.has(hit.barIndex)) return;
    seen.add(hit.barIndex);
    const date = dateForBarIndex(dates, hit.barIndex);
    if (!date) return;
    points.push({
      date,
      price: hit.price,
      position: hit.type === 'HIGH' ? 'above' : 'below',
      color: OUTCOME_COLORS.VERIFIED,
      label: `F(${p.fibNumber})`,
    });
  });

  return {
    type: 'pivotMarkers',
    id: SPIRAL_INSPECT_MARKS_IDS.FORWARD,
    points,
    color: ANCHOR_COLOR,
    label: 'Φ inspection',
    source: 'spiralInspect',
  };
}

/**
 * A curve from the anchor to each rung that landed on a turn.
 *
 * <p>Only VERIFIED rungs get an arc. Drawing one to every rung would fill the chart with lines to
 * bars where nothing happened, and the arcs are the part that reads as a claim of relationship.
 */
export function buildForwardArcOverlay(inspection, dates) {
  const anchor = inspection?.anchor;
  const projections = inspection?.projections;
  if (!anchor || !Array.isArray(projections)) return null;

  const fromDate = dateForBarIndex(dates, anchor.barIndex);
  if (!fromDate) return null;

  const arcs = [];
  projections.forEach((p) => {
    if (p.outcome !== 'VERIFIED' || !p.matchedPivot) return;
    const toDate = dateForBarIndex(dates, p.matchedPivot.barIndex);
    if (!toDate || toDate === fromDate) return;
    arcs.push({
      date1: fromDate,
      price1: anchor.price,
      date2: toDate,
      price2: p.matchedPivot.price,
      fibNumber: p.fibNumber,
    });
  });

  if (arcs.length === 0) return null;
  return {
    type: 'spiralArcs',
    id: SPIRAL_INSPECT_ARCS_IDS.FORWARD,
    arcs,
    color: ARC_COLOR,
    label: 'Φ lineage',
    source: 'spiralInspect',
  };
}

/**
 * BACKWARD: a marker on each parent turn, and one on the bar being traced.
 *
 * <p>The traced bar may not exist yet - tracing the upcoming zone is the main use - in which case
 * only the parents are marked. That is the honest depiction: the parents are real bars, the target
 * is a date the chart has not reached.
 */
export function buildBackwardMarkOverlay(inspection, dates) {
  const parents = inspection?.parents;
  if (!Array.isArray(parents) || parents.length === 0) return null;

  const points = [];
  const seen = new Set();
  parents.forEach((p) => {
    if (seen.has(p.pivotBarIndex)) return;
    seen.add(p.pivotBarIndex);
    const date = dateForBarIndex(dates, p.pivotBarIndex);
    if (!date) return;
    const isHigh = p.pivotType === 'HIGH';
    points.push({
      date,
      price: p.pivotPrice,
      position: isHigh ? 'above' : 'below',
      color: isHigh ? PARENT_HIGH_COLOR : PARENT_LOW_COLOR,
      label: `F(${p.fibNumber})`,
    });
  });

  if (points.length === 0) return null;
  return {
    type: 'pivotMarkers',
    id: SPIRAL_INSPECT_MARKS_IDS.BACKWARD,
    points,
    color: ARC_COLOR,
    label: 'Φ lineage',
    source: 'spiralInspect',
  };
}

/**
 * BACKWARD: a curve from each parent turn to the bar being traced.
 *
 * <p>When the traced bar is beyond the series, the arcs end at the last drawable bar - pointing at
 * where the date lies rather than pretending to reach it, the same compromise the scan's arcs make.
 */
export function buildBackwardArcOverlay(inspection, dates) {
  const parents = inspection?.parents;
  if (!Array.isArray(parents) || parents.length === 0) return null;
  if (!Array.isArray(dates) || dates.length === 0) return null;

  const lastIndex = dates.length - 1;
  const targetIndex = Math.min(inspection.target?.barIndex ?? lastIndex, lastIndex);
  const targetDate = dateForBarIndex(dates, targetIndex);
  if (!targetDate) return null;

  // The target's OWN price, so an arc ends where the bar it points at actually traded.
  //
  // Both ends used to be the parent's pivotPrice, which made every lineage arc horizontal: right
  // date, wrong height, arriving beside the target rather than on it. The backend now sends the
  // target's price — its pivot extreme when the bar is a turn, its close otherwise — and null past
  // the last bar, where there is no bar to have one.
  //
  // The backend now sends a price for a future target too — the last real close, carried forward,
  // which is the level the chart already paints those bars at. So the fallback below is no longer
  // the normal path for a forecast bar; it ends the arc where the candles actually are.
  //
  // It is KEPT rather than removed, for two reasons. The frontend and backend deploy separately,
  // so a new bundle can be talking to a backend that still sends null for a whole rollout window.
  // And a malformed or absent price must still degrade to a horizontal arc rather than to NaN,
  // which ECharts renders as a line to nowhere.
  const targetPrice = typeof inspection.target?.price === 'number'
    && Number.isFinite(inspection.target.price)
    ? inspection.target.price
    : null;

  const arcs = [];
  const seen = new Set();
  parents.forEach((p) => {
    if (seen.has(p.pivotBarIndex)) return;
    seen.add(p.pivotBarIndex);
    const fromDate = dateForBarIndex(dates, p.pivotBarIndex);
    if (!fromDate || fromDate === targetDate) return;
    arcs.push({
      date1: fromDate,
      price1: p.pivotPrice,
      date2: targetDate,
      price2: targetPrice != null ? targetPrice : p.pivotPrice,
      fibNumber: p.fibNumber,
    });
  });

  if (arcs.length === 0) return null;
  return {
    type: 'spiralArcs',
    id: SPIRAL_INSPECT_ARCS_IDS.BACKWARD,
    arcs,
    color: ARC_COLOR,
    label: 'Φ lineage',
    source: 'spiralInspect',
  };
}

/**
 * Everything to draw for one inspection, in draw order, for either mode.
 *
 * <p>Returns an empty array for a failed or empty inspection, so a caller can add the result
 * unconditionally and get nothing rather than a half-drawn study.
 */
export function buildInspectOverlays(inspection, barData) {
  const dates = barData?.dates;
  if (!inspection || inspection.ok === false) return [];
  if (!Array.isArray(dates) || dates.length === 0) return [];

  if (inspection.mode === 'BACKWARD') {
    return [
      buildBackwardArcOverlay(inspection, dates),
      buildBackwardMarkOverlay(inspection, dates),
    ].filter(Boolean);
  }
  return [
    buildForwardArcOverlay(inspection, dates),
    ...buildForwardLineOverlays(inspection, dates),
    buildForwardMarkOverlay(inspection, dates),
  ].filter(Boolean);
}

/**
 * Everything to draw for a forward ladder AND a backward lineage from the same bar.
 *
 * <p>Backward first, so the earlier turns and their arcs sit UNDER the forward ladder. The forward
 * rungs are the answer to "where does this bar point", which is the question the click was about;
 * the lineage is context for it.
 *
 * <p>Either half may be null — one request can fail or come back empty while the other succeeds —
 * and the result is the half that worked rather than nothing. A user who asked for both and got one
 * is better served by the one than by an empty chart.
 */
export function buildCombinedInspectOverlays(forward, backward, barData) {
  return [
    ...buildInspectOverlays(backward, barData),
    ...buildInspectOverlays(forward, barData),
  ];
}

/**
 * How many projected rungs fall past the last loaded bar.
 *
 * <p>Reported in words because they cannot be drawn. Silence would let a forward inspection look
 * like it found only a handful of targets when most of its ladder is simply off the right-hand
 * side of the chart.
 */
export function countUndrawable(inspection, dates) {
  const projections = inspection?.projections;
  if (!Array.isArray(projections)) return 0;
  const length = Array.isArray(dates) ? dates.length : 0;
  return projections.filter((p) => p.targetBarIndex < 0 || p.targetBarIndex >= length).length;
}

/**
 * The one-line summary shown above the rung table.
 *
 * <p>States the denominator. "3 of 7 verified" invites the reader to think the other four failed,
 * when some of them have not come due; naming the testable count is the difference between a hit
 * rate and a misleading fraction.
 */
export function formatForwardSummary(inspection) {
  const s = inspection?.summary;
  if (!s) return null;
  const parts = [`${s.verified} of ${s.testable} testable rungs landed on a turn`];
  if (s.future > 0) parts.push(`${s.future} still ahead`);
  return parts.join(' · ');
}
